/**
 * Playback control for `core/cover` background videos.
 *
 * Core Cover gives a background video no pause affordance at all, which fails
 * WCAG 2.2.2 for any loop running longer than five seconds. This adds the
 * control, honors `prefers-reduced-motion`, and starts deferred playback only
 * after the load event so the video does not compete for Largest Contentful Paint.
 *
 * `config/cover-video.php` strips `autoplay` and sets `preload="none"` on videos
 * that have a poster, marking them `data-cover-video-deferred`. Videos without a
 * poster keep core's autoplay — see the note in that file — and still get a
 * control here, so both paths are handled.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const STORAGE_PREFIX = 'sag:cover-video:';
const VIDEO_SELECTOR = '.wp-block-cover__video-background';

/**
 * Key playback preference by the video's path rather than its position, so a
 * FacetWP re-render or any other DOM replacement restores the same choice even
 * if the cover has moved in the document.
 */
const storageKey = (video, index) => {
  const source = video.currentSrc || video.getAttribute('src') || '';

  if (!source) {
    return `${STORAGE_PREFIX}index:${index}`;
  }

  try {
    return STORAGE_PREFIX + new URL(source, window.location.href).pathname;
  } catch (error) {
    return STORAGE_PREFIX + source;
  }
};

/**
 * Safari in private browsing throws on sessionStorage rather than returning null,
 * and a thrown preference lookup should never cost the visitor the control itself.
 */
const readPreference = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const writePreference = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    // Nothing to do: the control still works, it just will not be remembered.
  }
};

const buildButton = (video) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'o-cover-video__playback';
  button.setAttribute('data-cover-video-playback', '');

  if (!video.id) {
    video.id = `cover-video-${Math.random().toString(36).slice(2, 10)}`;
  }
  button.setAttribute('aria-controls', video.id);

  const icon = document.createElement('span');
  icon.className = 'material-symbols-outlined';
  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('data-cover-video-playback-icon', '');
  button.appendChild(icon);

  return button;
};

const enhance = (video, index) => {
  const cover = video.closest('.wp-block-cover');

  if (!cover || video.dataset.coverVideoEnhanced === 'true') {
    return;
  }
  video.dataset.coverVideoEnhanced = 'true';

  const key = storageKey(video, index);
  const motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  const wasDeferred = video.dataset.coverVideoDeferred === 'true';

  const button = buildButton(video);
  const icon = button.querySelector('[data-cover-video-playback-icon]');
  cover.appendChild(button);

  const syncButton = () => {
    const playing = !video.paused;

    // The accessible name states the action the button performs, and changes
    // with state, so the control is never conveyed by the icon alone.
    button.setAttribute('aria-pressed', playing ? 'false' : 'true');
    button.setAttribute(
      'aria-label',
      playing ? 'Pause the background video' : 'Play the background video'
    );

    if (icon) {
      icon.textContent = playing ? 'pause' : 'play_arrow';
    }
  };

  const start = () => {
    // Autoplay policy only permits a muted start, and core already sets both of
    // these. Reassert them so a filtered or hand-edited markup cannot break playback.
    video.muted = true;
    video.playsInline = true;

    if (video.preload === 'none') {
      video.preload = 'auto';
    }

    const attempt = video.play();

    if (attempt && typeof attempt.catch === 'function') {
      // A rejected play() is the browser's choice, not the visitor's, so it
      // updates the button without being written to the stored preference.
      attempt.catch(syncButton);
    }
  };

  video.addEventListener('play', syncButton);
  video.addEventListener('pause', syncButton);

  button.addEventListener('click', () => {
    if (video.paused) {
      writePreference(key, 'playing');
      start();
    } else {
      writePreference(key, 'paused');
      video.pause();
    }

    syncButton();
  });

  /*
   * A visitor who asked for reduced motion gets the poster and nothing else
   * until they opt in. An explicitly stored 'playing' IS that opt-in, so it
   * outranks the media query for the rest of the session.
   */
  const preference = readPreference(key);
  const shouldPlay = preference === 'playing' || (preference !== 'paused' && !motionQuery.matches);

  if (!shouldPlay) {
    video.pause();
  } else if (wasDeferred) {
    // Deferred until the page has finished loading, so the poster or the hero
    // copy remains the LCP element rather than the video.
    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }
  }

  // Someone can enable reduced motion mid-session. Respect it unless they have
  // already made an explicit choice for this video.
  const onMotionChange = (event) => {
    if (event.matches && !readPreference(key)) {
      video.pause();
    }
  };

  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', onMotionChange);
  }

  syncButton();
};

const CoverVideoBackground = (root = document) => {
  Array.from(root.querySelectorAll(VIDEO_SELECTOR)).forEach(enhance);
};

export default CoverVideoBackground;
